// GtSendFormModal — "Send a form to 120 people".
// Attach a saved form, write the email, check who will and will not get it
// (with one sample email), then send. Sending runs on the server at a steady
// pace, so this box can be closed; progress shows while it stays open.
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GtSelect } from './GtControls';
import GtEditor from './GtEditor';

const JSON_HEADERS = { 'Content-Type': 'application/json' };
const DEFAULT_BODY = '<p>Hi {first_name},</p><p>We have a few quick questions for you. It takes a couple of minutes, and the button below opens the form. No sign-in needed.</p><p>Thank you,<br>Generative Talent Co</p>';
const plural = (n, one, many) => n + ' ' + (n === 1 ? one : many);

export default function GtSendFormModal({ candidateIds, source, authFetch, onClose }) {
  const navigate = useNavigate();
  const [forms, setForms] = useState(null);
  const [formId, setFormId] = useState('');
  const [subject, setSubject] = useState('');
  const [subjectTouched, setSubjectTouched] = useState(false);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [closesOn, setClosesOn] = useState('');
  const [step, setStep] = useState('compose');
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null);
  const [progress, setProgress] = useState(null);
  const timer = useRef(null);
  const n = candidateIds.length;

  useEffect(() => {
    authFetch('/api/forms/options').then((r) => setForms(r.forms || [])).catch((e) => { setForms([]); setError(e.message); });
  }, [authFetch]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && !busy) onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const form = (forms || []).find((f) => String(f.id) === String(formId)) || null;
  const pickForm = (v) => {
    setFormId(v);
    const f = (forms || []).find((x) => String(x.id) === String(v));
    if (f && !subjectTouched) setSubject(f.title);
  };

  const payload = () => ({
    candidate_ids: candidateIds, subject: subject.trim() || (form && form.title) || '',
    body_html: body, closes_on: closesOn || null,
    source_kind: source && source.kind ? source.kind : 'candidates',
    source_id: source && source.id ? source.id : null,
    source_label: source && source.label ? source.label : null,
  });

  async function runCheck() {
    if (!form) { setError('Choose a form to attach.'); return; }
    setBusy(true); setError(null);
    try {
      const r = await authFetch('/api/forms/' + form.id + '/preview', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(payload()) });
      setCheck(r); setStep('check');
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function poll(sendId) {
    try {
      const r = await authFetch('/api/forms/sends/' + sendId);
      setProgress(r.send);
      if (r.send.status !== 'done') timer.current = setTimeout(() => poll(sendId), 1500);
    } catch (e) { timer.current = setTimeout(() => poll(sendId), 4000); }
  }

  async function send() {
    setBusy(true); setError(null);
    try {
      const r = await authFetch('/api/forms/' + form.id + '/send', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(payload()) });
      setSent(r); setStep('sending'); poll(r.send_id);
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  const done = progress && progress.status === 'done';
  const handled = progress ? progress.delivered + progress.undelivered : 0;
  const pct = progress && progress.total ? Math.round((handled / progress.total) * 100) : 0;
  const watch = () => { onClose(); navigate('/forms/' + form.id + '?scope=' + sent.send_id); };

  return (
    <div className="gtc-modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="gtc-modal gtsf-modal" role="dialog" aria-modal="true" aria-labelledby="gtsf-title" onClick={(e) => e.stopPropagation()}>
        <div className="gtc-modal-head gtsf-head" id="gtsf-title">
          <span>{step === 'compose' ? 'Send a form' : step === 'check' ? 'Check and send' : done ? 'Sent' : 'Sending'}</span>
          <span className="gtsf-to">{plural(n, 'person', 'people')}{source && source.label ? ' · ' + source.label : ''}</span>
        </div>

        <div className="gtc-modal-body gtsf-body">
          {error && <div className="gtnt-banner gtnt-banner--rose">{error}</div>}

          {step === 'compose' && (
            <>
              <div className="gtc-field"><span>Form to attach</span>
                {forms === null ? <div className="gtsf-muted">Loading your forms…</div> : forms.length === 0 ? (
                  <div className="gtsf-empty">You have no forms yet. <button type="button" className="gtsf-linkbtn" onClick={() => { onClose(); navigate('/forms'); }}>Build one in Forms</button></div>
                ) : (
                  <GtSelect value={formId} onChange={pickForm} placeholder="Choose a form"
                    options={forms.map((f) => ({ value: f.id, label: f.title + ' · ' + plural(f.question_count, 'question', 'questions') }))} />
                )}
              </div>
              {form && (
                <div className="gtsf-attached">
                  <span className="gtsf-clip" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5 12.5 20a5 5 0 0 1-7-7l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7L10.2 17.7a1.7 1.7 0 0 1-2.4-2.4l7.8-7.8" /></svg>
                  </span>
                  <div><b>{form.title}</b><span>Each person gets their own link. Their answers land on their record.</span></div>
                </div>
              )}
              <label className="gtc-field"><span>Subject</span>
                <input value={subject} maxLength={300} placeholder={form ? form.title : 'Subject line'}
                  onChange={(e) => { setSubject(e.target.value); setSubjectTouched(true); }} />
              </label>
              <div className="gtc-field"><span>Email</span>
                <div className="gtsf-editor"><GtEditor value={body} onChange={setBody} minHeight={230} /></div>
                <div className="gtsf-hint">Write <code>{'{first_name}'}</code> to use each person's first name. An <b>Open the form</b> button goes under your message.</div>
              </div>
              <label className="gtc-field gtsf-close"><span>Close the form on <em className="gtbn-opt">optional</em></span>
                <input type="date" value={closesOn} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setClosesOn(e.target.value)} />
              </label>
            </>
          )}

          {step === 'check' && check && (
            <>
              <div className="gtsf-tiles">
                <div className="gtsf-tile gtsf-tile--go"><b>{check.sendable}</b><span>will get it</span></div>
                <div className={'gtsf-tile' + (check.blocked.length ? ' gtsf-tile--warn' : '')}><b>{check.blocked.length}</b><span>will not</span></div>
                <div className="gtsf-tile"><b>{check.already.length}</b><span>already have it open</span></div>
              </div>
              {!check.sending_enabled && <div className="gtnt-banner gtnt-banner--amber"><b>Email is switched off on this server,</b> so nothing will be delivered.</div>}
              {check.blocked.length > 0 && (
                <details className="gtsf-list">
                  <summary>{plural(check.blocked.length, 'person', 'people')} will not get it, and why</summary>
                  <ul>{check.blocked.map((x) => <li key={x.id}><b>{x.name || 'Someone'}</b> {x.reason}</li>)}</ul>
                </details>
              )}
              {check.already.length > 0 && (
                <details className="gtsf-list">
                  <summary>{plural(check.already.length, 'person has', 'people have')} this form open and unanswered, so they are skipped</summary>
                  <ul>{check.already.map((x) => <li key={x.id}><b>{x.name || 'Someone'}</b></li>)}</ul>
                </details>
              )}
              {check.sample && (
                <div className="gtnt-email">
                  <div className="gtnt-meta">
                    <div><span>Sample</span><b>{check.sample.name} · {check.sample.to}</b></div>
                    <div><span>Subject</span><b>{check.sample.subject}</b></div>
                  </div>
                  <iframe className="gtnt-preview gtsf-preview" title="Sample email" sandbox="" srcDoc={
                    '<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;padding:18px 20px;font:14px/1.6 Inter,system-ui,sans-serif;color:#161618}</style></head><body>' + check.sample.html + '</body></html>'
                  } />
                </div>
              )}
              {check.sendable > 0 && <div className="gtsf-muted">Emails go out at a steady pace, about {check.seconds < 60 ? check.seconds + ' seconds' : Math.ceil(check.seconds / 60) + ' minutes'} for everyone. You can close this box once it starts.</div>}
            </>
          )}

          {step === 'sending' && (
            <div className="gtsf-progress">
              <div className="gtsf-bar"><i style={{ width: (done ? 100 : pct) + '%' }} /></div>
              {progress ? (
                <div className="gtsf-pnums">
                  <span><b>{progress.delivered}</b> delivered</span>
                  <span><b>{progress.undelivered}</b> not delivered</span>
                  <span><b>{progress.queued}</b> to go</span>
                  {progress.answered > 0 && <span className="gtsf-live"><i />{progress.answered} answered already</span>}
                </div>
              ) : <div className="gtsf-muted">Starting…</div>}
              {sent && sent.skipped && sent.skipped.length > 0 && (
                <div className="gtsf-muted">{plural(sent.skipped.length, 'person was', 'people were')} skipped because they already have this form open.</div>
              )}
              {!done && <div className="gtsf-muted">Sending carries on if you close this box.</div>}
              {done && <div className="gtnt-banner gtnt-banner--green"><b>All done.</b> Answers appear on each person's record and in the form's responses as they come in.</div>}
            </div>
          )}
        </div>

        <div className="gtc-modal-foot">
          {step === 'compose' && (
            <>
              <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={onClose}>Cancel</button>
              <button type="button" className="gtc-primary" disabled={busy || !form} onClick={runCheck}>{busy ? 'Checking…' : 'Check and preview'}</button>
            </>
          )}
          {step === 'check' && (
            <>
              <button type="button" className="gtc-btn-ghost" disabled={busy} onClick={() => setStep('compose')}>Back</button>
              <button type="button" className="gtc-primary" disabled={busy || !check || check.sendable === 0 ||!check.sending_enabled} onClick={send}>
                {busy ? 'Starting…' : 'Send to ' + check.sendable}
              </button>
            </>
          )}
          {step === 'sending' && (
            <>
              <button type="button" className="gtc-btn-ghost" onClick={onClose}>Close</button>
              <button type="button" className="gtc-primary" onClick={watch}>Watch responses</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
